import { Badge, Button, Card, Group, MultiSelect, Select, Stack, Text } from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import {
  type LlmModel,
  useLlmModels,
  useLlmTasks,
} from 'src/secret-menu/llm-dispatcher/hooks/useLlmCatalogue';

import { type FixtureVersion, type ReportError, useCreateArenaRun } from '../hooks/useModelArena';
import { countLabel, dateLabel } from '../runLabels';

import { sentenceCase } from './metricFormat';

import { groupCandidates, qualified } from './candidateOptions';

/** What the API accepts. Each candidate is measured over every document in turn, so the bound is
 * about time and spend rather than anything technical. */
const MOST_CANDIDATES = 12;

/** What a version is, in one line: what it holds, when it was published, and what the publisher said
 * changed. Enough to choose between two versions without opening W&B. */
const versionLabel = (version: FixtureVersion) => {
  const held =
    version.documents === null ? 'size unknown' : countLabel(version.documents, 'document');
  const published = version.createdAt ? dateLabel(version.createdAt) : 'date unknown';
  return `${version.version} · ${held} · ${published}${version.pinned ? ' · pinned' : ''}`;
};

const documentsDescription = (documentNames: string[], selected: string[]) => {
  if (documentNames.length === 0) {
    return 'W&B could not be listed, so specific documents cannot be offered — this run will measure on all of them';
  }
  if (selected.length === 0) {
    return 'Which documents to measure on. All of them by default — naming a few is a quicker, cheaper trial, but not comparable with a full leaderboard';
  }
  return 'Not the full set — these numbers are not comparable with a full leaderboard';
};

export function NewRunForm({
  taskKey,
  scoringModels,
  versions,
  onQueued,
  reportError,
}: {
  taskKey: string;
  scoringModels: string[];
  versions: FixtureVersion[];
  onQueued: () => void;
  reportError: ReportError;
}) {
  const { models } = useLlmModels();
  const { tasks: dispatcherTasks } = useLlmTasks();
  const { createRun, isQueueing } = useCreateArenaRun(reportError);
  const [selectedModelIds, setSelectedModelIds] = useState<string[]>([]);
  const [scoring, setScoring] = useState<Record<string, string>>({});
  const [fixtureDocuments, setFixtureDocuments] = useState<string[]>([]);
  const [notBefore, setNotBefore] = useState<string | null>(null);

  const pinned = versions.find(version => version.pinned);
  const [fixtureVersion, setFixtureVersion] = useState<string | null>(pinned?.version ?? null);
  const selectedVersion = versions.find(version => version.version === fixtureVersion);
  const measuringOnPinned = selectedVersion === undefined || selectedVersion.pinned;
  // Names, not a count: W&B lists them as part of the same call that says how many there are, so
  // offering them costs nothing extra — and a name is what lets a trial target one document rather
  // than however many happen to sort first.
  const documentNames = selectedVersion?.documentNames ?? [];

  const task = dispatcherTasks?.find(candidate => candidate.key === taskKey);
  const catalogue = models ?? [];
  const pooledIds = new Set(task?.pool.map(entry => entry.model.id) ?? []);
  const candidateCount = countLabel(selectedModelIds.length, 'candidate');
  const everyScoringModelPicked = scoringModels.every(name => scoring[name]);

  const submit = async () => {
    const queued = await createRun({
      taskKey,
      candidates: selectedModelIds.map(modelId => ({ modelId })),
      scoring,
      // Omitted when it is the pinned one, or when nothing is named, so the ordinary request stays
      // exactly what it was.
      fixtureVersion: measuringOnPinned ? undefined : (fixtureVersion ?? undefined),
      fixtureDocuments: fixtureDocuments.length === 0 ? undefined : fixtureDocuments,
      notBefore: notBefore ? new Date(notBefore).toISOString() : undefined,
    });
    if (!queued) return;

    const scoredBy = Object.values(scoring).join(', ');

    // The selection is deliberately kept: queueing the same candidates scored differently is a
    // likely next move, and an emptied form reads as one that never submitted.
    notifications.show({
      title: 'Run queued',
      message: scoredBy === '' ? candidateCount : `${candidateCount}, scored by ${scoredBy}`,
      color: 'teal',
    });
    onQueued();
  };

  return (
    <Card withBorder p="md" radius="sm">
      <Stack gap="sm">
        <MultiSelect
          label="Candidates"
          description="Any model in the catalogue can be compared, not only the ones this task already uses"
          placeholder={catalogue.length ? 'Pick models to compare' : 'No models in the catalogue'}
          data={groupCandidates(catalogue, task)}
          value={selectedModelIds}
          onChange={setSelectedModelIds}
          renderOption={({ option }) => (
            <Group gap="xs" justify="space-between" w="100%" wrap="nowrap">
              <Text size="sm">{option.label}</Text>
              {pooledIds.has(option.value) && (
                <Badge size="xs" color="teal" variant="light">
                  in pool
                </Badge>
              )}
            </Group>
          )}
          maxValues={MOST_CANDIDATES}
          searchable
          clearable
        />
        <SelectionSummary
          models={catalogue}
          selectedModelIds={selectedModelIds}
          pooledIds={pooledIds}
        />
        {/* One field per model the task says it is scored by — none for a task scored without one.
            Rendering what the task declares is what keeps adding a task out of this file. */}
        {scoringModels.map(name => (
          <Select
            key={name}
            label={sentenceCase(name)}
            description="What scores the run against gold. It shapes every number, so two runs scored differently are not comparable"
            placeholder="Pick the model to score with"
            data={catalogue.map(qualified).sort()}
            value={scoring[name] ?? null}
            onChange={picked => setScoring(chosen => ({ ...chosen, [name]: picked ?? '' }))}
            searchable
          />
        ))}
        {versions.length > 1 && (
          <>
            <Select
              label="Fixture set"
              description={
                measuringOnPinned
                  ? 'Which published version of the gold set to measure on'
                  : 'Not the pinned version — these numbers are not comparable with the rest of the leaderboard'
              }
              data={versions.map(version => ({
                value: version.version,
                label: versionLabel(version),
              }))}
              value={fixtureVersion}
              onChange={picked => {
                setFixtureVersion(picked);
                // Which documents exist belongs to the version, so a selection made against the
                // old one could name a document the new one does not have.
                setFixtureDocuments([]);
              }}
              allowDeselect={false}
            />
            {selectedVersion?.note && (
              <Text size="xs" c="dimmed">
                {selectedVersion.note}
              </Text>
            )}
          </>
        )}
        <MultiSelect
          label="Documents"
          description={documentsDescription(documentNames, fixtureDocuments)}
          placeholder={documentNames.length === 0 ? 'Every document' : 'All of them'}
          data={documentNames}
          value={fixtureDocuments}
          onChange={setFixtureDocuments}
          disabled={documentNames.length === 0}
          searchable
          clearable
        />
        <DateTimePicker
          label="Start"
          description="Leave empty to start as soon as a worker is free"
          placeholder="As soon as possible"
          value={notBefore}
          onChange={setNotBefore}
          clearable
        />
        <Group>
          <Button
            onClick={submit}
            loading={isQueueing}
            disabled={selectedModelIds.length === 0 || !everyScoringModelPicked}
          >
            Queue run
          </Button>
          {selectedModelIds.length > 0 && (
            <Text size="xs" c="dimmed">
              {candidateCount} · each runs the full pipeline over every fixture
            </Text>
          )}
        </Group>
      </Stack>
    </Card>
  );
}

/** The picked models as pills, so which are already in service stays visible after the dropdown closes. */
function SelectionSummary({
  models,
  selectedModelIds,
  pooledIds,
}: {
  models: LlmModel[];
  selectedModelIds: string[];
  pooledIds: Set<string>;
}) {
  if (selectedModelIds.length === 0) return null;

  return (
    <Group gap="xs">
      {models
        .filter(model => selectedModelIds.includes(model.id))
        .map(model => (
          <Badge
            key={model.id}
            size="sm"
            variant="light"
            color={pooledIds.has(model.id) ? 'teal' : 'grape'}
          >
            {qualified(model)}
          </Badge>
        ))}
    </Group>
  );
}
